<div class="tl-root">

  <!-- ═══════════════════════════════════════════════════
       LEFT: layer headers (fixed column, user-resizable)
  ════════════════════════════════════════════════════════ -->
  <div class="tl-left" [style.width.px]="layerPanelWidth" [style.min-width.px]="layerPanelWidth">

    <!-- Corner cell: height matches frame-header strip -->
    <div class="tl-corner">
      <button class="pxl-btn pxl-btn-ghost pxl-btn-xs" (click)="addLayer()" title="Add Layer">+ Layer</button>
    </div>

    <!-- One row per layer (rendered bottom → top so top layer is topmost visually) -->
    <ng-container *ngFor="let layer of (layers$ | async)?.slice()?.reverse(); trackBy: trackByLayerId; let i = index; let last = last">
      <div class="tl-layer-header"
           [class.tl-layer-active]="(activeLayerId$ | async) === layer.id"
           (click)="selectLayer(layer)">

        <!-- Vis / Lock toggles -->
        <div class="tl-layer-icons">
          <button class="icon-btn" [class.off]="!layer.visible" (click)="$event.stopPropagation(); toggleVisible(layer)" title="Toggle visible">👁</button>
          <button class="icon-btn" [class.on]="layer.locked"   (click)="$event.stopPropagation(); toggleLocked(layer)"  title="Toggle lock">🔒</button>
        </div>

        <!-- Color swatch (shows tint or checkerboard) -->
        <div class="tl-layer-swatch" [style.background]="layer.tintColor ?? 'transparent'">
          <span class="swatch-blend">{{ layer.blendMode === 'normal' ? '' : layer.blendMode.slice(0,3) }}</span>
        </div>

        <!-- Name -->
        <div class="tl-layer-name" (dblclick)="$event.stopPropagation(); startRename(layer)">
          <input *ngIf="editingLayerId === layer.id"
                 type="text" class="tl-layer-name-input"
                 [(ngModel)]="editingName"
                 (blur)="commitRename()"
                 (keydown.enter)="commitRename()"
                 (keydown.escape)="editingLayerId = null"
                 (click)="$event.stopPropagation()"
                 autofocus>
          <span *ngIf="editingLayerId !== layer.id">{{ layer.name }}</span>
        </div>

        <!-- Badges -->
        <div class="tl-layer-badges">
          <span *ngIf="layer.isContinuous" class="badge-cel"     title="Celluloid">🎞</span>
          <span *ngIf="layer.holdDuration > 1" class="badge-hold" title="Hold">×{{ layer.holdDuration }}</span>
          <span *ngIf="layer.opacity < 100" class="badge-opacity">{{ layer.opacity }}%</span>
        </div>

        <!-- Actions -->
        <div class="tl-layer-actions">
          <button class="icon-btn" (click)="$event.stopPropagation(); toggleExpandLayer(layer)" title="Properties">⚙</button>
          <button class="icon-btn" (click)="$event.stopPropagation(); moveLayerUp(layer)"   [disabled]="i === 0"   title="Move up">↑</button>
          <button class="icon-btn" (click)="$event.stopPropagation(); moveLayerDown(layer)" [disabled]="last" title="Move down">↓</button>
          <button class="icon-btn del" (click)="$event.stopPropagation(); deleteLayer(layer)" title="Delete layer">✕</button>
        </div>
      </div>

      <!-- Inline expanded properties (below the layer row) -->
      <div class="tl-layer-props" *ngIf="expandedLayerId === layer.id" (click)="$event.stopPropagation()">
        <div class="prop-row">
          <label>Opacity</label>
          <input type="range" [value]="layer.opacity" (input)="setOpacity(layer, $any($event.target).value)" min="0" max="100">
          <span>{{ layer.opacity }}%</span>
        </div>
        <div class="prop-row">
          <label>Blend</label>
          <select class="pxl-input pxl-input-sm" [value]="layer.blendMode" (change)="setBlend(layer, $any($event.target).value)">
            <option *ngFor="let m of blendModes" [value]="m">{{ m }}</option>
          </select>
        </div>
        <div class="prop-row">
          <label>Tint</label>
          <input type="color" [value]="layer.tintColor ?? '#000000'" (input)="setTint(layer, $any($event.target).value)"
                 style="width:28px;height:22px;border-radius:3px;cursor:pointer;border:1px solid var(--border);background:none;padding:1px;">
          <button *ngIf="layer.tintColor" class="pxl-btn pxl-btn-ghost pxl-btn-xs" (click)="setTint(layer, '#000000')" style="font-size:9px;">✕ Clear</button>
        </div>
        <div class="prop-row">
          <label>Hold</label>
          <input type="number" class="pxl-input pxl-input-sm" [value]="layer.holdDuration"
                 (change)="setHold(layer, $any($event.target).value)" min="1" max="24" style="width:50px;">
          <span style="font-size:9px;color:var(--text-dim)">frames</span>
        </div>
        <div class="prop-row">
          <label>Celluloid</label>
          <button class="pxl-btn pxl-btn-sm" [class.pxl-btn-primary]="layer.isContinuous" [class.pxl-btn-ghost]="!layer.isContinuous" (click)="toggleContinuous(layer)">
            {{ layer.isContinuous ? '🎞 On' : 'Off' }}
          </button>
        </div>
        <button class="pxl-btn pxl-btn-ghost pxl-btn-sm" [disabled]="(layers$ | async)?.indexOf(layer) === 0"
                (click)="mergeDown(layer)" style="margin-top:4px;width:100%">↓ Merge Down</button>
      </div>
    </ng-container>

    <!-- Onion skin section at bottom of layer headers -->
    <div class="tl-onion-section">
      <div class="tl-onion-header">
        <span class="pxl-section-label" style="font-size:10px;">🧅 Onion</span>
        <button class="pxl-btn pxl-btn-sm" [class.pxl-btn-primary]="onionEnabled" [class.pxl-btn-ghost]="!onionEnabled" (click)="toggleOnion()">
          {{ onionEnabled ? 'ON' : 'OFF' }}
        </button>
      </div>
      <div *ngIf="onionEnabled" class="tl-onion-props">
        <div class="prop-row">
          <label>Frames ±</label>
          <input type="range" [value]="onionFrames" (input)="updateOnionFrames($any($event.target).value)" min="1" max="5">
          <span>{{ onionFrames }}</span>
        </div>
        <div class="prop-row">
          <label>Opacity</label>
          <input type="range" [value]="onionOpacity" (input)="updateOnionOpacity($any($event.target).value)" min="5" max="60">
          <span>{{ onionOpacity }}%</span>
        </div>
        <div class="onion-legend">
          <span class="onion-prev">▌ Prev</span>
          <span class="onion-next">▌ Next</span>
        </div>
      </div>
    </div>
  </div><!-- /tl-left -->

  <!-- Resize handle between left and right panels -->
  <div class="tl-resize-handle"
       [class.tl-resize-handle-active]="resizing"
       (mousedown)="onResizeStart($event)"></div>


  <!-- ═══════════════════════════════════════════════════
       RIGHT: scrollable cel grid
  ════════════════════════════════════════════════════════ -->
  <div class="tl-right">

    <!-- Frame column headers (with composited thumbnail previews) -->
    <div class="tl-frame-headers">
      <ng-container *ngIf="(peers$ | async) as peers">
        <div *ngFor="let frame of (frames$ | async); trackBy: trackByFrameId; let i = index"
             class="tl-frame-header"
             [class.tl-frame-header-active]="(currentFrameId$ | async) === frame.id"
             [class.drag-over]="dragOverFrameIdx === i"
             [class.dragging]="dragSourceFrameIdx === i"
             draggable="true"
             (click)="selectFrame(frame.id)"
             (contextmenu)="onFrameContextMenu($event, frame.id)"
             (dragstart)="onFrameDragStart($event, i)"
             (dragover)="onFrameDragOver($event, i)"
             (dragleave)="onFrameDragLeave()"
             (drop)="onFrameDrop($event, i)"
             (dragend)="onFrameDragEnd()">

          <!-- Label row -->
          <div class="tl-frame-label">
            <span class="tl-frame-num">F{{ i + 1 }}</span>
            <button class="tl-frame-del" (click)="deleteFrame(frame.id, $event)" title="Delete frame"
                    [disabled]="!collabSvc.can('deleteFrame')">×</button>
            <!-- Peer dots -->
            <div class="tl-peer-dots" *ngIf="peersOnFrame(peers, frame.id).length > 0">
              <span *ngFor="let peer of peersOnFrame(peers, frame.id)"
                    class="tl-peer-dot" [style.background]="peer.color" [title]="peer.username">{{ peer.avatar }}</span>
            </div>
          </div>

          <!-- Composited frame thumbnail -->
          <div class="tl-frame-thumb-wrap">
            <canvas #frameCanvas
                    [attr.data-frame-id]="frame.id"
                    width="1" height="1"
                    class="tl-frame-thumb">
            </canvas>
          </div>
        </div>
      </ng-container>

      <!-- Add frame button -->
      <div class="tl-add-frame-btn" [class.disabled]="!collabSvc.can('createFrame')" (click)="addBlankFrame()" title="Add frame">+</div>
    </div><!-- /tl-frame-headers -->

    <!-- Cel grid (layers rows × frame cols) -->
    <div class="tl-cel-grid">
      <ng-container *ngFor="let layer of (layers$ | async)?.slice()?.reverse(); trackBy: trackByLayerId">

        <!-- One row = one layer, reversed so top layer is topmost -->
        <div class="tl-cel-row"
             [class.tl-cel-row-active]="(activeLayerId$ | async) === layer.id">

          <ng-container *ngFor="let frame of (frames$ | async); trackBy: trackByFrameId; let fi = index">
            <div class="tl-cel"
                 [class.tl-cel-active]="isActiveCel(frame.id, layer.id)"
                 [class.tl-cel-has-pixels]="celHasPixels(frame.id, layer.id)"
                 [class.tl-col-active-frame]="(currentFrameId$ | async) === frame.id"
                 (click)="onCelClick(frame.id, layer.id)"
                 (contextmenu)="onCelContextMenu($event, frame.id, layer.id)">

              <!-- Cel canvas thumbnail -->
              <canvas #celCanvas
                      [attr.data-frame-id]="frame.id"
                      [attr.data-layer-id]="layer.id"
                      width="1" height="1"
                      class="tl-cel-canvas"
                      [class.tl-cel-canvas-empty]="!celHasPixels(frame.id, layer.id)">
              </canvas>

              <!-- Active indicator dot -->
              <div class="tl-cel-active-dot" *ngIf="isActiveCel(frame.id, layer.id)"></div>
            </div>
          </ng-container>

          <!-- Empty spacer for + Add Frame column alignment -->
          <div class="tl-cel tl-cel-spacer"></div>
        </div>
      </ng-container>
    </div><!-- /tl-cel-grid -->

  </div><!-- /tl-right -->


  <!-- ═══════════════════════════════════════════
       Frame context menu
  ════════════════════════════════════════════════ -->
  <div class="tl-context-menu" *ngIf="contextMenuOpen"
       [style.left.px]="contextMenuX" [style.top.px]="contextMenuY"
       (click)="$event.stopPropagation()">
    <button class="tl-ctx-item"
            [disabled]="!canDuplicateCurrentFrame()"
            (click)="duplicateFrameFromMenu($event)">Duplicate frame</button>
  </div>

  <!-- ═══════════════════════════════════════════
       Cel context menu
  ════════════════════════════════════════════════ -->
  <div class="tl-context-menu" *ngIf="celContextMenuOpen"
       [style.left.px]="celContextMenuX" [style.top.px]="celContextMenuY"
       (click)="$event.stopPropagation()">
    <button class="tl-ctx-item" (click)="clearCelFromMenu()">Clear cel</button>
  </div>

</div>
